<template>
  <div class="detail-description" :class="{ 'is-en': !isChinese() }">
    <Breadcrumb :currentName="character?.name" class="detail-description__breadcrumb" />
    <div class="detail-description__avatar">
      <div class="detail-description__photo-wrap">
        <Resource
          class="detail-description__photo"
          :src="character?.image_url"
          :resource-option="{
            format: 'webp',
            resize: {
              w: 1000,
              m: 'lfit',
            },
          }"
        />
      </div>
    </div>
    <div class="detail-description__info">
      <div class="detail-description__info-container">
        <h1 class="detail-description__name">
          {{ character?.name }}
        </h1>
        <div class="detail-description__settings">
          <div class="detail-description__creation">
            <template v-if="character?.config?.age">
              <div>
                {{ $t('nian-ling') }}<i class="detail-colon">{{ colon }}</i>
                {{ character?.config?.age || 'xx' }}
              </div>
              <DividerVertical width="49px" height="16px" bg-color="var(--x-text-3)" />
            </template>
            <template v-if="character?.config?.gender">
              <div>
                {{ $t('xing-bie') }}<i class="detail-colon">{{ colon }}</i>
                {{ character?.config?.gender === 'Male' ? $t('nan') : $t('nv') }}
              </div>
              <DividerVertical width="49px" height="16px" bg-color="var(--x-text-3)" />
            </template>
            <template v-if="character?.online_time">
              <div>
                {{ $t('dan-sheng-ri-qi') }}<i class="detail-colon">{{ colon }}</i>
                {{ timeFormat(character?.online_time, 'YYYY-MM-DD') }}
              </div>
              <DividerVertical width="49px" height="16px" bg-color="var(--x-text-3)" />
            </template>
            <div v-if="character?.homepage_org_id" class="detail-description__creator">
              <span>
                {{ $t('chuang-jian-zhe') }}<i class="detail-colon">{{ colon }}</i>
              </span>
              &nbsp;
              <NuxtLink
                :to="{
                  name: PageKey.PERSONAL_PAGE,
                  params: { key: character?.homepage_org_id },
                }"
                class="detail-description__creator-link"
              >
                <Resource
                  class="detail-description__creator-avatar"
                  :src="character?.avatar_url"
                  :transform-resource="transformImage"
                />
                <span class="detail-description__creator-name">{{
                  character?.homepage_org_name
                }}</span>
              </NuxtLink>
            </div>
          </div>
          <p v-if="character?.description" class="detail-description__content">
            {{ $t('ren-wu-jie-shao') }}
            <i class="detail-colon">{{ colon }}</i>
            {{ character?.description }}
          </p>
          <div class="tags">
            <TagLight v-for="tag in tags" :key="tag.tag_id" :tag="tag" />
          </div>
        </div>
        <div class="detail-description__actions">
          <UpgradeButton
            :text="$t('mian-fei-shi-yong-ci-ren-wu')"
            :variant="'black-large'"
            class="detail-description__trail"
            :configType="character?.config_type"
            @handle="createWork"
          />
          <Tooltip placement="top" trigger="hover">
            <template #content>
              {{ character?.is_star_personal ? $t('qu-xiao-shou-cang') : $t('shou-cang') }}
            </template>
            <Collect
              v-if="character"
              :no-tip="true"
              :model-value="character?.is_star_personal"
              :params="{
                model_type: COLLECTION_MAP.SOURCE,
                model_id: character.id,
                resource_type: RESOURCE_TYPE.PERSON,
                source: COLLECTION_SOURCE.YOUYAN,
              }"
              @update:model-value="v => (character!.is_star_personal = v)"
            >
              <template #default="slotProps">
                <BaseBtn variant="circle-border-white-large">
                  <FavFillIcon v-if="slotProps.like" class="detail-description__icon" />
                  <FavIcon v-else class="detail-description__icon" />
                </BaseBtn>
              </template>
            </Collect>
          </Tooltip>
          <TemplateShare
            v-if="!character?.config?.is_special_outfit"
            :isCard="true"
            :params="{
              share_ids: [character?.id],
              model_type: COLLECTION_MAP.SOURCE,
            }"
            :trackParams="{
              source: resourceLocation?.source,
              id: character?.id,
              tags: character?.tags,
              look_name: character?.look_name,
              name: character?.name,
            }"
            :eventName="TRACK_EVENT.YOUYAN_PLATFORM_SHARE_CHARACTER"
            :shareToInnerUser="false"
            :linkUrl="`${location?.origin}/3d-avatar/${character?.identity}.html`"
            overlayClassName="ss-popover-custom"
            @open-change="popoverOpenChange"
          >
            <template #popContent>
              <div class="detail-description__share-popover">
                <p class="detail-description__share-title">
                  {{ $t('fen-xiang-3d-shu-zi-ren') }}
                </p>
                <BaseBtn
                  variant="black-middle"
                  class="detail-description__share-btn"
                  @click.stop="copy(`${location?.origin}/3d-avatar/${character?.identity}.html`)"
                >
                  {{ $t('fu-zhi-lian-jie') }}
                  <template #icon>
                    <LinkIcon class="detail-description__icon" />
                  </template>
                </BaseBtn>
              </div>
            </template>
            <Tooltip @open-change="tooltipOpenChange" :open="open.tooltip">
              <template #content>
                {{ $t('fen-xiang') }}
              </template>
              <BaseBtn variant="circle-border-white-large">
                <ShareIcon class="detail-description__icon cursor-pointer" />
              </BaseBtn>
            </Tooltip>
          </TemplateShare>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import UpgradeButton from '@/components/upgrade-button/index.vue';
import Collect from '@/features/collect/index.vue';
import { TemplateShare } from '@/features/template/components/templateShare';
import Tooltip from '@rendora/design/components/tooltip/Tooltip.vue';
import type { Character } from '~/api/interface/character';
import BaseBtn from '~/design/components/buttons/base.vue';
import FavFillIcon from '~/design/icons/favorite-filled.svg?component';
import FavIcon from '~/design/icons/favorite.svg?component';
import LinkIcon from '~/design/icons/link.svg?component';
import ShareIcon from '~/design/icons/share.svg?component';
import { transformImage } from '~/utils';

import Breadcrumb from '../Breadcrumb.vue';
import DividerVertical from '../divider-vertical.vue';
import TagLight from '../tag-light.vue';
import type { DetailTag } from './interface';

import { useI18n } from 'vue-i18n';
import { PageKey } from '~/constants/page';
import { useTime } from '~/hooks/useTime';
import { copyToClipboard } from '~/utils';

interface DetailDescriptionProps {
  character?: Character | null;
  tags: DetailTag[];
}

defineProps<DetailDescriptionProps>();

const _createWork = inject<(source?: SOURCE) => void>('createWork');
const _changeImage = inject<(source?: SOURCE) => void>('changeImage');

const resourceLocation = injectResourceLocation();
const { t: $t } = useI18n();
const share = useShare();

const colon = computed(() => (isChinese() ? '：' : ':'));

function createWork() {
  _createWork?.(resourceLocation?.source);
}

function changeImage() {
  _changeImage?.(resourceLocation?.source);
}

// 处理分享按钮的 popover + tooltip 互斥
const open = reactive({
  tooltip: false,
});

function popoverOpenChange(visible: boolean) {
  open.tooltip = false;
  share.value = visible;
}

function tooltipOpenChange(visible: boolean) {
  if (share.value) {
    return;
  }
  open.tooltip = visible;
}
const { timeFormat } = useTime();

const location = computed(() => {
  if (import.meta.client) {
    return window.location;
  }

  return null;
});

const handleCloseShare = () => {
  share.value = false;
};

const copy = (msg?: string, eventName?: TRACK_EVENT, trackParams?: any, onClose?: () => void) => {
  if (!msg) {
    return;
  }
  if (eventName) {
    trackEvent(eventName, {
      ...trackParams,
      share_mode: 2,
    });
  }
  handleCloseShare();
  try {
    copyToClipboard(msg);
    message.success($t('fu-zhi-cheng-gong'));
    onClose?.();
  } catch (error) {
    console.error(error);
    message.error($t('wen-ben-fu-zhi-shi-bai'));
  }
};
</script>
<style lang="scss">
.detail-description {
  display: flex;
  justify-content: center;
  position: relative;
  overflow: hidden;
  width: calc(100% - 10px);
  height: calc(100vh - 64px);
  min-height: 720px;
  min-width: 1440px;

  .tags {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-auto-flow: dense;
    column-gap: 16px;
    row-gap: 20px;
    @media (max-width: 1600px) {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  .detail-description__breadcrumb {
    position: absolute;
    top: 24px;
    left: 12.5%;
    z-index: 10;
  }
}

.detail-description__avatar {
  position: relative;
  height: 100%;
  width: 100%;
  background-color: var(--x-back-3);
  flex: 1;
  overflow: hidden;
  .detail-description__photo {
    pointer-events: none;
    aspect-ratio: 1 / 2;
    height: auto;
    width: 100%;
    object-fit: cover;
    object-position: top center;
    transform-origin: top center;
    transform: translateY(-2%);
  }
}

.is-en {
  .detail-colon {
    font-style: normal;
    margin-right: 4px;
  }
}
.detail-description__info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 24px;
  height: 100%;
  width: 100%;
  flex: 1;

  .detail-description__info-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 24px;
    width: 100%;
    padding: 0 100px;
  }

  .detail-description__name {
    color: #000;
    font-size: 48px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    margin-top: 0;
    margin-bottom: 0;
  }
  .detail-description__settings {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 24px;
    .detail-description__creation {
      display: flex;
      flex-wrap: wrap;
      color: var(--x-text-3);
      font-size: 16px;
      font-style: normal;
      line-height: 28px;
      a {
        font-size: 16px;
        &:hover {
          color: var(--x-purple-8);
        }
      }
    }
    .detail-description__creator {
      display: flex;
      align-items: center;
    }
    .detail-description__creator-link {
      display: flex;
      align-items: center;
    }
    .detail-description__creator-avatar {
      width: 18px;
      height: 18px;
    }
    .detail-description__creator-name {
      margin-left: 6px;
    }
  }
  .detail-description__content {
    color: var(--x-text-3);
    text-align: justify;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 28px; /* 175% */
    margin: 0;
  }
  .detail-description__actions {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 96px;
    .detail-description__trail {
      flex: 1;
    }
  }
}
.detail-description__share-popover-wrapper {
  .ant-popover-content {
    .ant-popover-arrow {
      display: none !important;
    }
  }
}
.detail-description__share-popover {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 344px;
  height: 124px;
  padding: 12px;
}
.detail-description__share-btn {
  width: 100%;
}
.detail-description__icon {
  width: 24px;
  height: 24px;
  &:deep {
    width: 24px;
    height: 24px;
  }
}
.detail-description__share-title {
  color: var(--x-text-1);
  text-align: center;
  margin: 0;
  font-size: var(--x-text-20-size);
  line-height: var(--x-text-20-line-height);
}
// Tooltip 样式
.avatar-detail-tooltip-custom {
  .ant-tooltip-content {
    transform: translateY(6px);
  }
  .ant-tooltip-arrow::before {
    background-color: #000;
  }
  .ant-tooltip-arrow::after {
    box-shadow: none;
  }
  .ant-tooltip-inner {
    padding: 8px 12px;
    font-size: 13px;
    line-height: 21px;
    text-align: center;
    border-radius: 8px;
    background-color: #000;
    box-shadow: none;
  }
}
</style>
